/// <amd-module name="Test/CloudTestLink/Components/CloudTestLink" />
import * as React from 'react';
import * as Common from 'Test/common-util/Common';
import { ObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, VssComponent, Components } from 'VSS/Platform/Layout';
import { Observer } from "VSSUI/Observer";
import { Dialog } from "VSSUI/Dialog";
import { Link } from "VSSUI/Link";
import { IListItemDetails, ListItem, List } from "VSSUI/List";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { IBuildRestClient } from "Build/Client/RestClient/Build";
import { getReleaseClient } from "Test/Release/Client/RestClient/Release";
import { getTFSPageData } from "Tfs/Platform/Page";

export interface ICloudTestLinkProps {
    artifactData : Common.IViewContextData;
}

export interface ICloudTestLinkState {
    links: CloudTestInfo[]
}

export class CloudTestLink extends VssComponent<ICloudTestLinkProps, ICloudTestLinkState, IVssComponentContext> {
    private isDialogOpen = new ObservableValue<boolean>(false);

    private onDismiss = () => {
        this.isDialogOpen.value = false;
    };
  
    private renderRow = (index: number, item: CloudTestInfo, details: IListItemDetails<CloudTestInfo>, key?: string): JSX.Element => {
        return (
        <ListItem index={index} details={details}>
            <Link href={CloudTestLink.getCloudTestTimelineLink(item)} target="_blank">{item.sessionId}</Link>
        </ListItem>);
    };
  
    constructor(props: ICloudTestLinkProps) {
        super(props);
        this.state = {} as ICloudTestLinkState
    }

    public componentDidMount() {
        try {
            if (this.props.artifactData.viewContext === Common.ViewContext.Build) {
                const buildClient = this.context.pageContext.getRestClient<IBuildRestClient>('IBuildRestClient');
                buildClient.getBuildTags(this._getProjectId(), this.props.artifactData.data.mainData.id).then((tags: string[]) => {
                    const info = CloudTestLink.getCloudTestInfoForPipeline(tags); 
                    this.setState({ links : info })
                }, (error: any) => {
                    console.warn(error);
                })
            }
            else if (this.props.artifactData.viewContext === Common.ViewContext.Release) {
                const releaseClient = getReleaseClient(this.context.pageContext);
                releaseClient.getReleaseTags(this._getProjectId(), this.props.artifactData.data.mainData.id).then((tags: string[]) => {
                    const attempts = this.props.artifactData.data.subData.environment.deploySteps.map((d: any) => d.attempt);
                    const currentAttempt =  Math.max(...attempts);
                    const releaseExecutionScope = `${this.props.artifactData.data.subData.environment.id}!${currentAttempt}`
                    const info = CloudTestLink.getCloudTestInfoForPipeline(tags, releaseExecutionScope);
                    this.setState({ links : info.filter( i => i.scope == releaseExecutionScope) })
                }, (error: any) => {
                    console.warn(error);
                })
            }
        }
        catch (error) {
            console.warn(error);
        }   
    }

    private _getProjectId(): string {
        let projectContext = getTFSPageData(this.context.pageContext);
        let projectId = "";
        if (projectContext && projectContext.project) {
            projectId = projectContext.project.id;
        }
        return projectId;
    }
  
    public render(): any {
        if (!this.state.links || this.state.links.length === 0) {
            return null
        }

        if (this.state.links.length === 1) {
            return (
                <Link className="cloud-test-execution-link" href={CloudTestLink.getCloudTestTimelineLink(this.state.links[0])} target="_blank">View CloudTest Execution</Link>
            )
        }

        return (
            <>
            <Link className="cloud-test-execution-link" onClick={() => { this.isDialogOpen.value = true; }}>View CloudTest Execution</Link>
            <Observer isDialogOpen={this.isDialogOpen}>
                {(props: {
                    isDialogOpen: boolean;
                }) => {
                    return props.isDialogOpen ? (<Dialog titleProps={{ text: "CloudTest Execution" }} footerButtonProps={[
                        {
                            text: "Close",
                            onClick: this.onDismiss
                        }
                    ]} onDismiss={this.onDismiss}>
                        <List itemProvider={new ArrayItemProvider<CloudTestInfo>(this.state.links)} renderRow={this.renderRow} width="100%" />
                    </Dialog>) : null;
                }}
            </Observer>
            </>
        );
    }

    private static getCloudTestInfoForPipeline(tags: string[], scope?: string): CloudTestInfo[] {
        const cloudTestInfoList: CloudTestInfo[] = []
        const cloudTestProdEnvironmentName = "Prod";

        if (!tags) {
            return cloudTestInfoList;
        }

        tags.forEach(tag => {
            const cloudTestPrefix = 'CloudTest_'
            if (tag && tag.startsWith(cloudTestPrefix)) {
                const cloudTestInfo = tag.slice(cloudTestPrefix.length);
                const parts = cloudTestInfo.split('_');
                if (parts.length == 1){
                    cloudTestInfoList.push({ sessionId: parts[0], environment: cloudTestProdEnvironmentName });
                }
                else if (parts.length == 2){
                    CloudTestLink.isCloudTestInternalEnvironment(parts[1]) 
                        ? cloudTestInfoList.push({ sessionId: parts[0], environment: parts[1]})
                        : cloudTestInfoList.push({ sessionId: parts[0], environment: cloudTestProdEnvironmentName, scope: parts[1]})
                }
                else if (parts.length == 3){
                    cloudTestInfoList.push({ sessionId: parts[0], environment: parts[1], scope: parts[2]});
                }
            }
        });

        return cloudTestInfoList.filter(i => i.scope == scope);
    }

    private static isCloudTestInternalEnvironment(environment: string): boolean {
        return environment.toUpperCase() === "PPE" || environment.toUpperCase() === "DEV"
    }

    private static getCloudTestTimelineLink(info: CloudTestInfo): string {
        return `https://dev.azure.com/cloudtest/_apps/hub/cloudtest.hub.hub#timeline/${info.sessionId}?env=${info.environment}`
    }
}

interface CloudTestInfo {
    sessionId: string
    environment: string  
    scope?: string
}

Components.add("cloud-test-link", CloudTestLink);